/**
 * The four keys a call answers to.
 *
 * | Ctrl+M | mute, and unmute |
 * | Space held | talk, while push to talk is on — except on the board |
 * | Ctrl+Shift+B | a mark, and a toast saying so |
 * | Escape | nothing |
 *
 * **Escape leaves nothing.** It closes a menu and it does not end a call — a
 * key people press to dismiss a popover must never be the key that hangs up on
 * somebody, and the one place it is handled is the menu that opened.
 *
 * Space is held, not toggled, so it is `keydown` and `keyup` rather than a
 * click: `setPushToTalk(true)` while it is down, `false` when it comes up, and
 * the repeat events an auto-repeating key sends in between are dropped. A
 * `blur` releases it as well, because a window that loses focus mid-word would
 * otherwise keep the microphone open.
 *
 * **The board keeps its Space.** Held on the board's canvas it pans, and in a
 * meeting with push to talk on it used to open the microphone as well. An
 * element marked `OWNS_SPACE` keeps Space for itself while it has focus; every
 * other key still reaches the call, and a Space let go there still closes a
 * microphone it opened somewhere else.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CallSession } from '../../lib/calls/types';

export interface PushToTalk {
  /** The person's choice. Off by default: a call where the microphone is live
   *  is what everybody expects. */
  enabled: boolean;
  /** Whether the key is down right now. The indicator reads this. */
  held: boolean;
}

export interface CallKeys {
  pushToTalk: PushToTalk;
  setPushToTalkEnabled: (on: boolean) => void;
}

/** Marks an element whose Space is its own while it has focus. */
export const OWNS_SPACE = 'data-owns-space';

/** True when the keystroke belongs to something being typed into. A composer
 *  with focus owns its own space bar. */
function typing(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

function ownsSpace(target: EventTarget | null): boolean {
  const el = target as Element | null;
  return typeof el?.closest === 'function' && el.closest(`[${OWNS_SPACE}]`) !== null;
}

export interface KeyHandlers {
  down: (e: KeyboardEvent) => void;
  up: (e: KeyboardEvent) => void;
  /** The window lost focus. */
  release: () => void;
}

/** The listeners `useCallKeys` installs on the window, apart from React so
 *  they can be driven without a DOM. */
export function callKeyHandlers({
  session,
  pushToTalk,
  setHeld,
  onMark,
  onChange
}: {
  session: Pick<CallSession, 'local' | 'mark' | 'setMuted' | 'setPushToTalk'>;
  /** Whether push to talk is on, read when the key arrives. */
  pushToTalk: () => boolean;
  setHeld: (held: boolean) => void;
  onMark?: (() => void) | undefined;
  onChange?: (() => void) | undefined;
}): KeyHandlers {
  const down = (e: KeyboardEvent) => {
    if (typing(e.target)) return;

    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      void session.mark().then(() => onMark?.());
      return;
    }

    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'm') {
      e.preventDefault();
      session.setMuted(!session.local.muted);
      onChange?.();
      return;
    }

    if (e.code === 'Space' && pushToTalk() && !ownsSpace(e.target)) {
      e.preventDefault();
      if (e.repeat) return;
      session.setPushToTalk(true);
      setHeld(true);
      onChange?.();
    }
  };

  const up = (e: KeyboardEvent) => {
    if (e.code !== 'Space' || !pushToTalk()) return;
    session.setPushToTalk(false);
    setHeld(false);
    onChange?.();
  };

  const release = () => {
    if (!pushToTalk()) return;
    session.setPushToTalk(false);
    setHeld(false);
    onChange?.();
  };

  return { down, up, release };
}

export function useCallKeys({
  session,
  enabled,
  onMark,
  onChange
}: {
  session: CallSession;
  /** False outside a live call: a ring is not a place to press Ctrl+M. */
  enabled: boolean;
  onMark?: () => void;
  /** Called after a key changed the session, so a caller re-reads `local`. */
  onChange?: () => void;
}): CallKeys {
  const [ptt, setPtt] = useState<PushToTalk>({ enabled: false, held: false });
  const ptrEnabled = useRef(false);
  ptrEnabled.current = ptt.enabled;

  const setPushToTalkEnabled = useCallback(
    (on: boolean) => {
      setPtt({ enabled: on, held: false });
      // Turning it on mutes until the key is held; turning it off leaves the
      // microphone live, which is the state the button then shows.
      if (on) session.setPushToTalk(false);
      else session.setMuted(false);
      onChange?.();
    },
    [session, onChange]
  );

  useEffect(() => {
    if (!enabled) return;

    const { down, up, release } = callKeyHandlers({
      session,
      pushToTalk: () => ptrEnabled.current,
      setHeld: (held) => setPtt((p) => ({ ...p, held })),
      onMark,
      onChange
    });

    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', release);
    };
  }, [session, enabled, onMark, onChange]);

  return { pushToTalk: ptt, setPushToTalkEnabled };
}
